<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <filter-manager
      ref="filterRef"
      v-model="filterModel"
      class="mb-4"
      :filters="filtersConfig"
      @academicYear="onChangeAcademicYear"
      @provider="onProviderChange"
      @search="onSearchHandler"
      @serviceType="onServiceTypeChange"
    />
    <custom-separator class="my-4" text="Resultado de Lugares de Servicio" />
    <custom-result-not-found
      v-if="results.length == 0"
      :text="'No cuenta con información disponible.'"
    />
    <template v-else>
      <location-service-result-row
        v-for="(row, idx) of results"
        :key="`result-${idx}`"
        class="mb-3"
        :data="row"
        :is-medicaid-documentation-workflow-enabled="
          isMedicaidDocumentationWorkflowEnabled
        "
        @on-view-location-service="onViewLocationServiceHandler(row)"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        show-text
        @pagination-go-page="goToPageHandler"
      />
    </template>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import Pagination from 'pagination';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import LocationServiceResultRow from './components/LocationServiceResultRow';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';
import { getByParameterName } from '@/services/common/sysparameter.api';
import {
  getInitFilters as _getInitFilters,
  search as _search,
} from '@/services/provider/location-service.api.js';
import { getProvidersByAcademicYear as _getProvidersByAcademicYear } from '@/services/provider/provider.api.js';
import { getDisciplineTypes as _getDisciplineTypes } from '@/services/common/code.api.js';
import {
  getMunicipalities as _getMunicipalities,
  getMunicipalitiesByProvider as _getMunicipalitiesByProvider,
} from '@/services/common/location.api.js';
import storeModuleNames from '@/utils/constants/storeModuleNames.js';
import roles from '@/utils/constants/roles.js';

const DEFAULT_PAGE_SIZE = 20;
const LOCATION_SERVICE_MANAGMENT_ROUTE_NAME = 'locationServiceManagement';

export default {
  name: 'LocationServiceManagementMain',
  mixins: [alertMixin, storeMixin],
  components: {
    PageHeader,
    Pagination,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    LocationServiceResultRow,
  },
  data: () => ({
    moduleName: storeModuleNames.ProviderLocationServiceModuleName,
    filterModel: {
      academicYear: null,
      provider: null,
      municipality: null,
      serviceType: null,
      disciplineType: null,
      locationType: null,
      status: null,
    },
    isMedicaidDocumentationWorkflowEnabled: false,
    filterOptions: {
      academicYears: [],
      providers: [],
      municipalities: [],
      serviceTypes: [],
      disciplineTypes: [],
      locationTypes: [],
      statuses: [],
    },
    municipalities: [],
    pagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    results: [],
  }),
  computed: {
    headerConfig() {
      return {
        title: 'Lugares de Servicio',
        icon: 'fas fa-user-tie',
        routes: ['Inicio', 'Proveedor', 'Lugares de Servicio'],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Añadir',
          icon: 'fas fa-file-plus pr-2',
          variant: 'success ml-0',
          visible: this.canAddLocationService,
          click: () => this.onAddLocationServiceHandler(),
        },
      ];
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: false,
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filterOptions.providers,
          placeholder: 'Proveedor',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-6',
          validations: 'required',
          error: 'El nombre del proveedor es requerido.',
          disabled: this.disableProviderDropdown,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filterOptions.statuses,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.filterOptions.municipalities,
          placeholder: 'Seleccione',
          label: 'Municipio:',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          class: 'col-12 col-md-3',
        },
        {
          name: 'serviceType',
          component: 'CustomDropDown',
          options: this.filterOptions.serviceTypes,
          placeholder: 'Seleccione',
          label: 'Tipo de Servicio:',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          class: 'col-12 col-md-3',
        },
        {
          name: 'disciplineType',
          component: 'CustomDropDown',
          options: this.filterOptions.disciplineTypes,
          placeholder: 'Seleccione',
          label: 'Disciplina:',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          class: 'col-12 col-md-3',
        },
        {
          name: 'locationType',
          component: 'CustomDropDown',
          options: this.filterOptions.locationTypes,
          placeholder: 'Seleccione',
          label: 'Tipo de Lugar:',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          class: 'col-12 col-md-3',
        },
      ];
    },
    canAddLocationService() {
      return this.userHasPermissions('rempro.providers.locationservices.add');
    },
    hasProviderRole() {
      return this.userRoleId === roles.Provider_RP;
    },
    disableProviderDropdown() {
      return this.userHasSingleCompany && this.filterModel.provider != null;
    },
    userHasSingleCompany() {
      return this.userCompanies?.length === 1;
    },
    userCurrentCompany() {
      return this.userHasSingleCompany ? this.userCompanies[0] : null;
    },
    hasSpecialistRpRole() {
      return this.fullProfile?.roleid?.toUpperCase() === roles.Specialist_RP;
    },
  },
  async mounted() {
    await this.getParams();
    await this.getInitFilters();
    if (this.moduleData?.LocationServiceManagementMain)
      await this.onSearchHandler(this.filterModel);
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async getParams() {
      const { data } = await getByParameterName(
        'MedicaidDocumentationWorkflowEnabled'
      );

      this.isMedicaidDocumentationWorkflowEnabled =
        data?.value?.toLowerCase() === 'true';
    },
    async getInitFilters() {
      const { filters, collections } = (await _getInitFilters()).data;
      if (Object.values(filters).length && Object.values(collections).length) {
        const status = filters.estatus;
        delete filters.estatus;

        this.filterModel = {
          ...this.filterModel,
          ...filters,
          status,
        };
        this.filterOptions = {
          ...this.filterOptions,
          ...collections,
        };

        if (this.hasProviderRole) {
          await this.onProviderChange(this.userCurrentCompany);
          return;
        }

        this.municipalities = collections.municipalities;
      }
    },
    async getMunicipalities(academicYearId) {
      this.filterModel.municipality = null;
      this.filterOptions.municipalities = (
        await _getMunicipalities(academicYearId)
      ).data;
    },
    async onChangeAcademicYear(value) {
      if (value?.id) {
        await this.getMunicipalities(value.id);
      }
    },
    async onProviderChange(value) {
      this.filterModel.municipality = null;
      if (value?.id && this.filterModel.academicYear?.id) {
        const academicYearId = this.filterModel.academicYear.id;
        const { data } = await _getMunicipalitiesByProvider(
          academicYearId,
          value.id
        );
        this.filterOptions.municipalities = data;

        return;
      }

      this.filterOptions.municipalities = this.municipalities;
    },
    async onServiceTypeChange(value) {
      this.filterModel.disciplineType = null;
      this.filterOptions.disciplineTypes = [];
      if (value?.id) {
        this.filterOptions.disciplineTypes = (
          await _getDisciplineTypes(value.id)
        ).data;
      }
    },
    async onAddLocationServiceHandler() {
      const isValid = await this.$refs.filterRef.$refs.formValidator.validate();
      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }

      const companyId = this.filterModel.provider?.id;
      this.$router.push({
        name: LOCATION_SERVICE_MANAGMENT_ROUTE_NAME,
        params: {
          companyId,
        },
      });
    },
    async onSearchHandler(pageIndex, payload) {
      const isValid = await this.$refs.filterRef.$refs.formValidator.validate();
      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }
      if (!payload) {
        payload = this.fillPayload(pageIndex);
      }
      this.searchLocationService(pageIndex, payload);
    },
    async searchLocationService(page, payload) {
      const res = (await _search(payload)).data;
      this.results = res.data;
      this.pagination = {
        ...this.pagination,
        count: res.count,
        currentPage: res.currentPage,
        pageCount: res.pageCount,
        totalItemCount: res.totalItemCount,
      };
    },
    async goToPageHandler(page) {
      const payload = this.fillPayload(page);
      await this.onSearchHandler(page, payload);
    },
    fillPayload(page) {
      const payload = {
        academicYear: this.filterModel.academicYear?.id,
        provider: this.filterModel.provider?.id,
        municipality: this.filterModel.municipality?.id,
        serviceType: this.filterModel.serviceType?.id,
        discipline: this.filterModel.disciplineType?.id,
        locationType: this.filterModel.locationType?.id,
        status:
          this.filterModel.status?.id == 0
            ? true
            : this.filterModel.status?.id == 1
            ? false
            : null,
        pageIndex: page ? page : 1,
        pageSize: this.pagination.pageSize,
      };
      return payload;
    },
    onViewLocationServiceHandler({ companyId, locationFiscalYearId }) {
      if (companyId && locationFiscalYearId) {
        this.$router.push({
          name: LOCATION_SERVICE_MANAGMENT_ROUTE_NAME,
          params: {
            companyId,
            locationFiscalYearId,
          },
        });
      }
    },
  },
  watch: {
    async 'filterModel.academicYear'(newValue) {
      if (this.hasSpecialistRpRole) {
        return;
      }

      const { data } = await _getProvidersByAcademicYear(newValue.id);
      this.filterOptions.providers = data;
      this.filterModel.provider = null;

      if (this.hasProviderRole) {
        const currentCompanyIsSelectable = data.find(
          (provider) => provider.id == this.userCurrentCompany.id
        );
        this.filterOptions.providers = currentCompanyIsSelectable
          ? [this.userCurrentCompany]
          : [];
        this.filterModel.provider = currentCompanyIsSelectable
          ? this.userCurrentCompany
          : null;
      }
    },
  },
};
</script>
